import { queryOptions } from '@tanstack/react-query';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { LocateAccount } from '@thinkalpha/ui-types/contracts/locates/platforms';
/* eslint-disable-next-line no-restricted-imports -- legacy direct import */
import { getLocatesAccounts } from 'src/queries/http/locates-ws/platforms/discover';
/* eslint-disable-next-line no-restricted-imports -- legacy direct import */
import { getLocatesProviders } from 'src/queries/http/locates-ws/providers';

export const locatesAccountsBaseQueryKey = 'locates-accounts';
export type LocatesAccountsQueryKey = [typeof locatesAccountsBaseQueryKey, string[] | undefined];

/**
 * Generates a query to get all locates accounts for the given account IDs.
 * If accountIds is undefined, fetches all locates accounts.
 *
 * @param accountIds Optional array of account IDs to filter by
 * @returns a query object with queryKey and queryFn
 */
export function getLocatesAccountsQuery(accountIds?: string[]) {
    return queryOptions({
        queryKey: [locatesAccountsBaseQueryKey, accountIds] satisfies LocatesAccountsQueryKey,
        queryFn: () => getLocatesAccounts(accountIds),
    });
}

export const locatesProvidersBaseQueryKey = 'locates-providers';
export type LocatesProvidersQueryKey = [typeof locatesProvidersBaseQueryKey];

/**
 * Generates a query to get all locates providers.
 *
 * @returns a query object with queryKey and queryFn
 */
export function getLocatesProvidersQuery() {
    return queryOptions({
        queryKey: [locatesProvidersBaseQueryKey] satisfies LocatesProvidersQueryKey,
        queryFn: () => getLocatesProviders(),
    });
}

/**
 * Generates a query to get locates providers that support quotes.
 * Shares the same cache as getLocatesProvidersQuery and filters via `select`.
 *
 * @returns a query object with queryKey, queryFn, and select
 */
export function getLocatesQuoteProvidersQuery() {
    return queryOptions({
        queryKey: [locatesProvidersBaseQueryKey] satisfies LocatesProvidersQueryKey,
        queryFn: () => getLocatesProviders(),
        select: (providers) => providers.filter((provider) => provider.supportsQuotes),
    });
}

/**
 * Combines user accounts with locates accounts to create LocateAccount objects.
 * This merges permission information from user accounts with canInject information from locates accounts.
 *
 * @param userAccounts Array of user accounts
 * @param locatesAccounts Array of locates accounts
 * @returns Combined array of LocateAccount objects
 */
export function combineUserAndLocatesAccounts(
    userAccounts: ConcreteAccount[],
    locatesAccounts: LocateAccount[],
): LocateAccount[] {
    const convertedAccounts = userAccounts.map((acc) => {
        const locateAccount = locatesAccounts.find((locAcc) => locAcc.id === acc.id);

        return {
            ...acc,
            permissions: {
                ...acc.permissions,
                canRead: true,
            },
            canInject: locateAccount?.canInject ?? false,
        } satisfies LocateAccount;
    });

    const combinedAccounts = [
        ...convertedAccounts,
        ...locatesAccounts.filter((acc) => !convertedAccounts.some((userAcc) => userAcc.id === acc.id)),
    ];

    return combinedAccounts;
}
